import SwiperLib, { ReactIdSwiperProps, SwiperInstance } from '@yanolja-rnd/react-swiper';
import clsx from 'clsx';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { Autoplay, Lazy, Navigation, Pagination } from 'swiper/dist/js/swiper.esm';
import styles from './Swiper.module.scss';
import useNavigation from './useNavigation';

export interface ISwiperProps extends ReactIdSwiperProps {
  /**
   * swiper 안 item의 타입
   * default - 상품. 썸네일의 세로중앙에 네비 위치
   * banner - 높이의 세로중앙에 위치(50%)
   * tab - 탭메뉴(커스텀 버튼)
   */
  itemType?: string;
  isRenewal?: boolean;
  className?: string;
  placeholders?: React.ReactNode;
  style?: CSSProperties;
}

/**
 * 기본 설정.
 *
 * 중앙 정렬, 카드 간격 10px.
 */
export const baseSwiperConfig: ReactIdSwiperProps = {
  centeredSlides: true,
  preloadImages: false,
  runCallbacksOnInit: false,
  slidesPerView: 'auto',
  spaceBetween: 10,
  threshold: 10,
  updateOnImagesReady: false,
};

/**
 * SSR 결과물에는 `@yanolja-rnd/react-swiper`를 적용할 수 없으므로 하이드레이션 후 스타일이 눈에 띄게 변할 수 있다.
 *
 * `Swiper` 컴포넌트는 하이드레이션 이전까지 CSS로 `@yanolja-rnd/react-swiper`를 적용한 레이아웃을 흉내낸다.
 */
const Swiper: React.FC<ISwiperProps> = ({
  children,
  className,
  itemType,
  isRenewal,
  getSwiper,
  placeholders,
  style,
  ...config
}) => {
  const swiperRef = useRef<SwiperInstance>();
  const [isMounted, setMounted] = useState(false);
  const childrenLength = React.Children.count(children);
  const { navigationConfig, prevBtn, nextBtn } = useNavigation({ itemType, isRenewal, navigation: config.navigation });

  const init = (swiper: SwiperInstance): void => {
    getSwiper && getSwiper(swiper);
    swiperRef.current = swiper;

    setTimeout(() => {
      swiperRef.current?.update();
    }, 100);
  };

  useEffect(() => {
    setMounted(childrenLength > 1);
  }, [childrenLength]);

  const swiperConfig = {
    ...config,
    slidesOffsetBefore: config.centeredSlides ? 0 : config.slidesOffsetBefore || 0,
    slidesOffsetAfter: config.centeredSlides ? 0 : config.slidesOffsetAfter || 0,
    navigation: navigationConfig,
  };

  return (
    <div className={clsx(styles.container, isMounted && styles.containerMounted, className)} style={style}>
      <div
        className={clsx(
          'swiper-container',
          'swiper-placeholder',
          styles.placeholder,
          config.centeredSlides && styles.placeholderCentered,
        )}
        style={{
          marginLeft: `${swiperConfig.slidesOffsetBefore || 0}px`,
          ['--margin' as any]: `${swiperConfig.spaceBetween || 0}px`,
        }}
      >
        {placeholders || children}
      </div>
      {childrenLength > 1 && (
        <div className={clsx(styles.swiper, !swiperConfig.slidesOffsetBefore && styles.tab, 'swiper-lib')}>
          <SwiperLib
            getSwiper={init}
            modules={[Autoplay, Lazy, Navigation, Pagination]}
            renderPrevButton={prevBtn}
            renderNextButton={nextBtn}
            {...swiperConfig}
          >
            {children}
          </SwiperLib>
        </div>
      )}
    </div>
  );
};

export default Swiper;
